import { useEffect, useState } from "react";
import { kg, type Dossier, type KgCompany } from "../api";
import { List } from "./ui";

// Associer la conversation (via sa personne) à une entreprise, pour le contexte.
// Affiche l'entreprise déjà liée (dossier) + un sélecteur d'entreprise existante
// OU la création d'une nouvelle, puis kg.linkCompany(personId, companyId).
export function CompanyLink({
  personId,
  dossier,
  onChanged,
}: {
  personId: string;
  dossier: Dossier | null;
  onChanged: () => void;
}) {
  const [companies, setCompanies] = useState<KgCompany[]>([]);
  const [value, setValue] = useState(""); // saisie libre (nom) — match insensible à la casse
  const [url, setUrl] = useState(""); // association « par URL » (logo + domaine auto)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    kg.companies()
      .then((r) => setCompanies(r.companies))
      .catch(() => setCompanies([]));
  }, []);

  const linked = new Set((dossier?.companies ?? []).map((c) => c.name.toLowerCase()));

  async function associate() {
    const name = value.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Entreprise existante (match nom insensible à la casse) sinon création.
      const existing = companies.find((c) => c.name.toLowerCase() === name.toLowerCase());
      const companyId = existing ? existing.id : (await kg.createCompany(name)).companyId;
      await kg.linkCompany(personId, companyId);
      setValue("");
      const r = await kg.companies();
      setCompanies(r.companies);
      onChanged(); // rafraîchit le dossier → l'entreprise apparaît dans « Entreprise »
    } catch {
      setError("L'association a échoué.");
    } finally {
      setBusy(false);
    }
  }

  // Association « par URL » : le backend déduit le domaine, scrape le logo et
  // rattache le contact en un appel. Idéal pour reconnaître ensuite les emails.
  async function associateUrl() {
    const u = url.trim();
    if (!u || busy) return;
    setBusy(true);
    setError(null);
    try {
      await kg.createCompanyFromUrl(u, personId);
      setUrl("");
      onChanged();
    } catch {
      setError("Impossible de lire cette URL (ou domaine privé).");
    } finally {
      setBusy(false);
    }
  }

  return (
    <List header="Associer une entreprise">
      <div className="company-link">
        <input
          list="kg-company-list"
          className="company-input"
          value={value}
          placeholder="Nom de l'entreprise…"
          aria-label="Nom de l'entreprise"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void associate();
            }
          }}
        />
        <datalist id="kg-company-list">
          {companies.map((c) => (
            <option key={c.id} value={c.name} />
          ))}
        </datalist>
        <button
          type="button"
          className="company-add"
          disabled={busy || !value.trim() || linked.has(value.trim().toLowerCase())}
          onClick={() => void associate()}
        >
          {busy ? "…" : linked.has(value.trim().toLowerCase()) ? "Déjà liée" : "Associer"}
        </button>
      </div>
      <div className="company-link">
        <input
          className="company-input"
          type="url"
          value={url}
          placeholder="…ou colle l'URL du site (logo auto)"
          aria-label="URL du site de l'entreprise"
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void associateUrl();
            }
          }}
        />
        <button
          type="button"
          className="company-add"
          disabled={busy || !url.trim()}
          onClick={() => void associateUrl()}
        >
          {busy ? "…" : "Par URL"}
        </button>
      </div>
      {error && <p className="info-empty">{error}</p>}
    </List>
  );
}
